import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";

import { CaseDiagnosis, CptInput } from "custom-models";

import { FeatureFlags } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import PermanentlyResizableTextarea from "components/viewer/PermanentlyResizableTextarea";
import { selectAutoCptCodesEnabled } from "selectors/autoCptCodesEnabled";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import {
  cptString,
  createApiCptList,
  reduceCptCodes,
} from "../DiagnosisDialogHelpers";
import AutoCPTCodes, {
  cptChargesForSpecimen,
} from "../dialog-inputs/AutoCPTCodes";
import CPTInputGroup from "../dialog-inputs/CPTInputGroup";
import ICD10Input from "../dialog-inputs/ICD10Input";
import ZSDInput from "../dialog-inputs/ZSDInput";
import Sidebar from "../Sidebar";
import SidebarFooter from "../SidebarFooter";
import { createListString, isDigitizationCode } from "../utils";

//TODO: The view is pretty much the same as DiagnosisDialog.tsx. Look into merging these two components.

type ModifyDiagnosisViewProps = {
  hat: string;
  caseId: number;
  addonDiagnosis: Partial<CaseDiagnosis>;
  completeDiagnosis: CaseDiagnosis;
  onUpdateDiagnosis: Function;
  onUpdateDiagnosisCpt: Function;
  onResetDiagnosis?: Function;
  onCancelDiagnosis?: Function;
  checkDiagnosisDifference?: Function;
  cptCodes: CptInput[];
  setCptCodes: Function;
  displayFlatDiagnosis?: boolean;
  isCorrection?: boolean;
  isModMedCase?: boolean;
};

const ModifyDiagnosisView = ({
  hat,
  caseId,
  addonDiagnosis,
  completeDiagnosis,
  onUpdateDiagnosis,
  onUpdateDiagnosisCpt,
  onResetDiagnosis,
  onCancelDiagnosis,
  checkDiagnosisDifference,
  cptCodes,
  setCptCodes,
  displayFlatDiagnosis,
  isCorrection,
}: ModifyDiagnosisViewProps) => {
  const navigate = useNavigate();
  const caseDetailObject = useAppSelector(({ caseDetail }) => caseDetail);
  const showScreenDisclaimerToggle =
    (addonDiagnosis.source === CaseDiagnosis.source.AMEND ||
      addonDiagnosis.source === CaseDiagnosis.source.FINALIZE) &&
    caseDetailObject?.screen_requests?.length > 0 &&
    caseDetailObject?.screen_assignment;
  const specimens = caseDetailObject.specimens;
  const specimen = specimens?.find((s) => s.id === completeDiagnosis.specimen);

  const [commonWidth, setCommonWidth] = useLocalStorage<number | undefined>(
    "DIAGNOSIS_TEXTAREA_WIDTH"
  );

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const [cptError, setCptError] = useState<boolean>(false);
  const [icd10Error, setIcd10Error] = useState<boolean>(false);
  const [zsdError, setZsdError] = useState<boolean>(false);
  useState<number>(0);

  const checkErrors = (
    error?: boolean,
    errorType: "zsd" | "icd10" | "cpt" | "" = ""
  ) => {
    return (
      error ||
      (errorType === "zsd" ? error : zsdError) ||
      (errorType === "cpt" ? error : cptError) ||
      (errorType === "icd10" ? error : icd10Error)
    );
  };

  const completeDiagnosisCptStr = reduceCptCodes(
    completeDiagnosis.cpt_codes || []
  )
    .reduce((acc: string[], cptInput) => {
      if (cptInput.code.code) {
        acc.push(`${cptString(cptInput.code)}(${cptInput.quantity || 1})`);
      }
      return acc;
    }, [])
    .join(", ");

  const handleOnResetDiagnosis = () => {
    onResetDiagnosis && onResetDiagnosis();
    setCptCodes(reduceCptCodes(completeDiagnosis.cpt_codes || []));
  };

  const handleOnViewPreview = () => {
    navigate(`/cases/${caseId}/preview`);
  };

  const naturalCptCodes = cptCodes.filter(
    (cptCode) => !isDigitizationCode(cptCode)
  );

  const useAutoCptCodes = useAppSelector(selectAutoCptCodesEnabled);

  return (
    <Sidebar
      hasAutoWidth
      isScrollable
      hat={
        <div className="FinalizedDiagnosis__header FinalizedDiagnosis__header--edit">
          <FontAwesomeIcon icon="pencil" />
          <span>{hat}</span>
        </div>
      }
    >
      <div className="FinalizedDiagnosis Sidebar__contentContainer">
        <div className="DiagnosisDialog__diagnosis diagnosis__section">
          <header className="DiagnosisDialog__header">
            <label
              className="diagnosis__sectionLabel"
              htmlFor="diagnosis-textarea"
            >
              Diagnosis
            </label>
          </header>
          {(isCorrection || displayFlatDiagnosis) &&
            completeDiagnosis.diagnosis && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosis.diagnosis}
              </span>
            )}
          {!isCorrection && (
            <PermanentlyResizableTextarea
              id="diagnosis-textarea"
              rows={5}
              value={addonDiagnosis.diagnosis || ""}
              onChange={(_e) => {
                onUpdateDiagnosis("diagnosis", _e.target.value);
              }}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          )}
        </div>
        <div className="DiagnosisDialog__findings diagnosis__section">
          <header className="DiagnosisDialog__header">
            <label
              className="diagnosis__sectionLabel"
              htmlFor="microscopic-textarea"
            >
              Microscopic Findings
            </label>
          </header>
          {(isCorrection || displayFlatDiagnosis) &&
            completeDiagnosis.microscopic && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosis.microscopic}
              </span>
            )}
          {!isCorrection && (
            <PermanentlyResizableTextarea
              id="microscopic-textarea"
              rows={5}
              value={addonDiagnosis.microscopic || ""}
              onChange={(_e) => {
                onUpdateDiagnosis("microscopic", _e.target.value);
              }}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          )}
          {showScreenDisclaimerToggle && (
            <label className="checkbox__label" style={{ width: commonWidth }}>
              <input
                type="checkbox"
                //@ts-ignore
                checked={!!addonDiagnosis.is_screen_valid}
                onChange={(_e) => {
                  onUpdateDiagnosis(
                    "is_screen_valid",
                    //@ts-ignore
                    !addonDiagnosis.is_screen_valid
                  );
                }}
              />
              Dr. {caseDetailObject?.screen_assignment?.first_name}{" "}
              {caseDetailObject?.screen_assignment?.last_name}, a board
              certified dermatopathologist, has reviewed the case and concurs.
            </label>
          )}
        </div>
        <div className="DiagnosisDialog__comments diagnosis__section">
          <header className="DiagnosisDialog__header">
            <label
              className="diagnosis__sectionLabel"
              htmlFor="comments-textarea"
            >
              Note
            </label>
          </header>
          {(isCorrection || displayFlatDiagnosis) &&
            completeDiagnosis.comment && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosis.comment}
              </span>
            )}
          {!isCorrection && (
            <PermanentlyResizableTextarea
              id="comments-textarea"
              rows={5}
              value={addonDiagnosis.comment || ""}
              onChange={(_e) => {
                onUpdateDiagnosis("comment", _e.target.value);
              }}
              commonWidth={commonWidth}
              setCommonWidth={setCommonWidth}
            />
          )}
        </div>
        <div className="DiagnosisDialog__codes">
          <div className="diagnosis__section">
            {displayFlatDiagnosis ? (
              <>
                <label className="diagnosis__sectionLabel">ICD10</label>
                <span className="readOnly__section">
                  {completeDiagnosis.icd10_code}
                </span>
              </>
            ) : (
              <ICD10Input
                icd10={addonDiagnosis.icd10_code || ""}
                icd10Error={icd10Error}
                setIcd10={(newValue: string) => {
                  onUpdateDiagnosis("icd10_code", newValue.toUpperCase());
                }}
                setIcd10Error={setIcd10Error}
              />
            )}
          </div>
          <div className="diagnosis__section">
            {displayFlatDiagnosis ? (
              <>
                <label className="diagnosis__sectionLabel">ICD10</label>
                <span className="readOnly__section">
                  {completeDiagnosis.zsd_code}
                </span>
              </>
            ) : (
              <ZSDInput
                zsd={addonDiagnosis.zsd_code || ""}
                zsdError={zsdError}
                setZsd={(newValue: string) => {
                  onUpdateDiagnosis("zsd_code", newValue);
                }}
                setZsdError={setZsdError}
              />
            )}
          </div>
          <div className="diagnosis__section">
            <label className="diagnosis__sectionLabel">CPT</label>
            {displayFlatDiagnosis && completeDiagnosisCptStr && (
              <span
                className="readOnly__section"
                style={{ maxWidth: commonWidth }}
              >
                {completeDiagnosisCptStr}
              </span>
            )}
            {useAutoCptCodes ? (
              <>
                {specimen && (
                  <AutoCPTCodes
                    caseDetail={caseDetailObject}
                    specimen={specimen}
                  />
                )}
              </>
            ) : (
              <>
                <CPTInputGroup
                  cptCodes={naturalCptCodes}
                  setCptCodes={(newValue: CptInput[]) => {
                    setCptCodes(newValue);
                    onUpdateDiagnosisCpt(
                      createApiCptList(newValue.filter((c) => !c.error))
                    );
                  }}
                  setCptError={setCptError}
                  displayLabel={false}
                  glassRead={
                    specimen?.glass_read || caseDetailObject?.glass_read
                  }
                  currentDiagnosisCptCodes={addonDiagnosis.cpt_codes}
                />
                {features?.previewAutoCPTCodes &&
                  specimen &&
                  caseDetailObject.charge_caches &&
                  cptChargesForSpecimen(caseDetailObject, specimen) && (
                    <AutoCPTCodes
                      caseDetail={caseDetailObject}
                      specimen={specimen}
                      preview={true}
                    />
                  )}
              </>
            )}
          </div>
        </div>
      </div>
      <SidebarFooter>
        <div className="FinalizedDiagnosis__actionsContainer">
          {checkErrors(false) && (
            <span data-testid="error-span" className="diagnosisForm__formError">
              Unrecognised{" "}
              {createListString([
                ...(icd10Error ? ["ICD10"] : []),
                ...(zsdError ? ["ZSD"] : []),
                ...(cptCodes.some((cpt) => cpt.error) ? ["CPT"] : []),
              ])}{" "}
              code
            </span>
          )}
          <div className="FinalizedDiagnosis__actions">
            {onCancelDiagnosis && (
              <PathologyWatchButton
                onClick={() => {
                  onCancelDiagnosis(addonDiagnosis?.id);
                }}
                color="red"
                className="FinalizedDiagnosis__actions--cancel"
              >
                Cancel Edit
              </PathologyWatchButton>
            )}
            {onResetDiagnosis && (
              <PathologyWatchButton
                onClick={() => {
                  handleOnResetDiagnosis();
                }}
                disabled={
                  checkDiagnosisDifference &&
                  !checkDiagnosisDifference(
                    completeDiagnosis,
                    addonDiagnosis,
                    cptCodes
                  )
                }
              >
                Reset Diagnosis
              </PathologyWatchButton>
            )}
            <PathologyWatchButton
              color="green"
              onClick={() => {
                handleOnViewPreview();
              }}
            >
              Preview for Sign Out &rarr;
            </PathologyWatchButton>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default ModifyDiagnosisView;
